import React, { useState, useEffect, useRef } from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Navigation, Lazy } from "swiper"
import "swiper/swiper-bundle.min.css"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import { AppFunctionComponent } from "../../../../../types"
import { getAllModules, QuickStartModule } from "./utils"
import { Logo } from "../../../../../navigation/components/social/social.styled"
import logo from "../../../../../images/layout/logo-simple.svg"

// Initialize Swiper modules
SwiperCore.use([Navigation, Lazy])

const ControlsPrevIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M13.2929 6.05078L7.58594 11.7578L13.2929 17.4648L14.7069 16.0508L10.4139 11.7578L14.7069 7.46478L13.2929 6.05078Z"
      fill="black"
    />
  </svg>
)

const ControlsNextIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M9.70697 17.707L15.414 12L9.70697 6.29297L8.29297 7.70697L12.586 12L8.29297 16.293L9.70697 17.707Z"
      fill="black"
    />
  </svg>
)
const SliderButtonStyle = styled.button<{
  disabledNext?: boolean
  disabledPrev?: boolean
  color?: string
}>`
  display: flex;
  padding: 8px 20px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  border: 2px solid ${({ color }) => color ?? "#000"};
  font-family: Facto;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-transform: uppercase;
  background: transparent;
  color: ${({ color }) => color ?? "#000"};
  transition: opacity 0.3s ease-in-out;
  cursor: pointer;

  svg {
    path {
      fill: ${({ color }) => color ?? "#000"};
    }
  }

  ${({ disabledPrev, disabledNext }) =>
    (disabledPrev || disabledNext) &&
    css`
      border: 2px solid #494949;
      color: #494949;
      opacity: 0.5;
      cursor: not-allowed;
      svg {
        path {
          fill: #494949;
        }
      }
    `}
`

const SliderContainer = styled.div`
  padding: 5rem 0;
  border-radius: 1.6rem;
  max-width: 1260px;
  margin: 0 auto;
  width: calc(100% - 30px);

  @media (max-width: 768px) {
    padding: 0 15px;
    margin: 0;
    max-width: 100%;
  }
`

const SliderContent = styled.div`
  max-width: 440px;
`

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 70px;
  }
`

const SliderTitle = styled.h2`
  font-family: Facto;
  font-size: 23px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 27.6px */
`

const SliderDescription = styled.p`
  font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`

const SearchDescription = styled.p`
  font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 20px;
`

const SearchInputContainer = styled.div`
  position: relative;
`

const SearchInput = styled.input`
  display: flex;
  padding: 12px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid black;
  background: #fff;
  padding-left: 60px;
  min-width: 280px;

  color: #505050;

  font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */

  &:focus {
    outline: none;
    border-color: #3182ce;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 15px - 60px - 32px);
    min-width: unset;
  }
`

const SearchIcon = styled.svg`
  position: absolute;
  left: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  height: 1.5rem;
  width: 1.5rem;
  color: black;
`

const CardsContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`

const Card = styled(Link)`
  display: block;
  width: 288px;

  flex-shrink: 0;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #dedcd9;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;

  &:hover {
    box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    width: 99%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    height: 350px;
  }
`

const ImageContainer = styled.div`
  position: relative;
  height: 242px;
  width: 100%;
  background-color: #f7fafc;

  @media (max-width: 768px) {
    height: 280px;
  }
`

const CardContent = styled.div`
  display: flex;
  padding: 32px 24px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`

const CardTitle = styled.h3`
  font-family: Facto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  color: black;
  text-align: center;
  margin: 0;
`

const StyledControlsButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const SwiperLazyPreloader = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;

  &.swiper-lazy-preloader {
    border: none !important;
    animation: none !important;
    border-radius: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
`

const NoResultsFound = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  text-align: center;
  color: #494949;
  border-radius: 12px;
  padding: 20px;

  p {
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 36px */
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    width: unset;
  }
`

export const QuickStartSlider: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [filteredModules, setFilteredModules] = useState<QuickStartModule[]>([])
  const [swiper, setSwiper] = useState<SwiperCore>()
  const [activeIndex, setActiveIndex] = useState(0)
  const swiperRef = useRef<HTMLDivElement | null>(null)
  const [swiperKey, setSwiperKey] = useState(0)
  const { translate, language } = useTranslationHook()

  // Determine if buttons should be shown based on screen size and number of filtered modules
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener("resize", checkIsMobile)

    return () => {
      window.removeEventListener("resize", checkIsMobile)
    }
  }, [])

  // Determine if next/prev buttons should be disabled
  const isPrevButtonDisabled = activeIndex === 0
  const isNextButtonDisabled = isMobile
    ? activeIndex >= filteredModules.length - 1
    : filteredModules.length <= 4 || activeIndex >= filteredModules.length - 4

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    // Just increment the key to force re-render
    setSwiperKey((prev) => prev + 1)
  }

  // Handle search input key press (for Enter key)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Blur the input to hide the keyboard on mobile
      e.currentTarget.blur()
    }
  }

  useEffect(() => {
    const allModules = getAllModules(language)

    if (!searchQuery.trim() || searchQuery.length < 3) {
      setFilteredModules(allModules)
    } else {
      const lowerQuery = searchQuery.toLowerCase()
      setFilteredModules(
        allModules.filter(
          (module) =>
            module.title.toLowerCase().includes(lowerQuery) ||
            module.description.toLowerCase().includes(lowerQuery) ||
            module.sectionTitle.toLowerCase().includes(lowerQuery) ||
            module.content.toLowerCase().includes(lowerQuery)
        )
      )
    }

    // Reset active index when filtered modules change
    setActiveIndex(0)
    // Don't reset the swiper instance here
  }, [searchQuery])

  // Handle swiper initialization and cleanup
  useEffect(() => {
    return () => {
      // Cleanup swiper when component unmounts
      if (swiper) {
        swiper.destroy(true, true)
        setSwiper(undefined)
      }
    }
  }, [])

  // Update active index when swiper changes slides
  useEffect(() => {
    if (swiper && typeof swiper.activeIndex === "number") {
      setActiveIndex(swiper.activeIndex)
    }
  }, [swiper?.activeIndex])

  return (
    <SliderContainer id="quick-start-slider">
      <SearchContainer>
        <SliderContent>
          <SliderTitle>{translate("qs-kompakt-slider-title")}</SliderTitle>
          <SliderDescription>
            {translate("qs-kompakt-slider-description")}
          </SliderDescription>
        </SliderContent>
        <div>
          <SearchDescription>
            {translate("qs-kompakt-search-label")}
          </SearchDescription>
          <SearchInputContainer>
            <SearchInput
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder={translate("qs-kompakt-search-placeholder")}
            />
            <SearchIcon
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </SearchIcon>
          </SearchInputContainer>
        </div>
      </SearchContainer>

      <CardsContainer>
        {filteredModules.length > 0 ? (
          <Swiper
            key={swiperKey}
            slidesPerView={1}
            threshold={4}
            spaceBetween={16}
            ref={swiperRef}
            preloadImages
            lazy={{
              loadPrevNext: true,
              loadPrevNextAmount: 4,
              checkInView: true,
              loadOnTransitionStart: true,
            }}
            onSwiper={(swiperInstance) => {
              if (swiperInstance) {
                setSwiper(swiperInstance)
                // Force load initial slides
                setTimeout(() => {
                  if (swiperInstance && swiperInstance.lazy) {
                    swiperInstance.lazy.load()
                  }
                }, 100)
              }
            }}
            onSlideChange={(swiperInstance) => {
              if (
                swiperInstance &&
                typeof swiperInstance.activeIndex === "number"
              ) {
                setActiveIndex(swiperInstance.activeIndex)
              }
            }}
            navigation={{
              nextEl: ".swiper-arrow-right",
              prevEl: ".swiper-arrow-left",
            }}
            breakpoints={{
              768: {
                slidesPerView: 4,
                spaceBetween: 16,
                slidesPerGroup: 1,
              },
            }}
          >
            {filteredModules.map((module, index) => (
              <SwiperSlide
                style={{ height: !isMobile ? "352px" : "" }}
                key={`${module.sectionId}-${module.id}`}
              >
                <Card
                  to={`${
                    language === "pl" ? "/pl" : ""
                  }/products/phones/mudita-kompakt/quick-start/${
                    module.sectionId
                  }/${module.id}`}
                >
                  <ImageContainer>
                    <StyledImage
                      src={index < 4 ? module.image : undefined}
                      data-src={module.image}
                      alt={module.title}
                      className="swiper-lazy"
                    />
                    <SwiperLazyPreloader className="swiper-lazy-preloader"></SwiperLazyPreloader>
                  </ImageContainer>
                  <CardContent>
                    <CardTitle>{module.title}</CardTitle>
                  </CardContent>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <NoResultsFound>
            <Logo width={200} src={logo} alt="Mudita" />
            <p>
              No results found for <strong>"{searchQuery}"</strong>. Please try
              another search term.
            </p>
          </NoResultsFound>
        )}
      </CardsContainer>

      {filteredModules.length > 0 && (
        <StyledControlsButtonWrapper>
          <SliderButtonStyle
            className="swiper-arrow-left"
            disabledPrev={isPrevButtonDisabled}
            onClick={() => {
              if (swiper && !isPrevButtonDisabled) {
                if (isMobile) {
                  const objectPosition =
                    swiperRef.current?.getBoundingClientRect()
                  if (objectPosition) {
                    window.scrollTo(
                      objectPosition.left,
                      objectPosition.top + window.scrollY - 220
                    )
                  }
                }
              }
            }}
          >
            <ControlsPrevIcon />
            {translate("PREV")}
          </SliderButtonStyle>
          <SliderButtonStyle
            className="swiper-arrow-right"
            disabledNext={isNextButtonDisabled}
            onClick={() => {
              if (swiper && !isNextButtonDisabled) {
                if (isMobile) {
                  const objectPosition =
                    swiperRef.current?.getBoundingClientRect()
                  if (objectPosition) {
                    window.scrollTo(
                      objectPosition.left,
                      objectPosition.top + window.scrollY - 220
                    )
                  }
                }
              }
            }}
          >
            {translate("NEXT")}
            <ControlsNextIcon />
          </SliderButtonStyle>
        </StyledControlsButtonWrapper>
      )}
    </SliderContainer>
  )
}
